import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useMemo, useState } from "react";
import { Loader2, Mail, Paperclip, Search, Send, X } from "lucide-react";
import { filesToAttachments, type PickedAttachment } from "@/lib/email/attach-files";
import { toast } from "sonner";
import {
  adminGetThread,
  adminListInbox,
  adminSendDirectEmail,
  adminSendReply,
} from "@/lib/api/admin-inbox.functions";
import {
  MATTER_CATEGORIES, PORTAL_LINK, presetById, presetsByGroup,
  renderTemplate, type TemplateVars,
} from "@/lib/email-presets";


export const Route = createFileRoute("/_authenticated/portal/admin/inbox")({
  head: () => ({
    meta: [
      { title: "Admin · Inbox" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AdminInbox,
});

type Tab = "all" | "sent_emails" | "contact_messages" | "consultation_bookings" | "awareness_bookings" | "contract_orders";

const TABS: { id: Tab; label: string }[] = [
  { id: "all", label: "All" },
  { id: "sent_emails", label: "Sent emails" },
  { id: "contact_messages", label: "Enquiries" },
  { id: "consultation_bookings", label: "Consultations" },
  { id: "awareness_bookings", label: "Awareness" },
  { id: "contract_orders", label: "Contract orders" },
];

function AdminInbox() {
  const qc = useQueryClient();
  const list = useServerFn(adminListInbox);
  const thread = useServerFn(adminGetThread);
  const send = useServerFn(adminSendReply);
  const sendDirect = useServerFn(adminSendDirectEmail);

  const [tab, setTab] = useState<Tab>("all");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState<{ source: any; id: string } | null>(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeTo, setComposeTo] = useState("");
  const [composeToName, setComposeToName] = useState("");
  const [composeSubject, setComposeSubject] = useState("");
  const [composeBody, setComposeBody] = useState("");
  const [replyFiles, setReplyFiles] = useState<PickedAttachment[]>([]);
  const [composeFiles, setComposeFiles] = useState<PickedAttachment[]>([]);
  const [replyCategory, setReplyCategory] = useState("general");
  const [composeCategory, setComposeCategory] = useState("general");

  function applyPreset(
    id: string,
    setSubj: (v: string) => void,
    setBod: (v: string) => void,
    setCat: (v: string) => void,
    vars: TemplateVars = {},
  ) {
    const p = presetById(id);
    if (!p) return;
    const merged: TemplateVars = {
      Service: MATTER_CATEGORIES.find((c) => c.value === p.category)?.label,
      PortalLink: PORTAL_LINK,
      ...vars,
    };
    setSubj(renderTemplate(p.subject, merged));
    setBod(renderTemplate(p.body, merged));
    setCat(p.category);
  }


  async function pickFiles(
    list: FileList | null,
    setter: (updater: (prev: PickedAttachment[]) => PickedAttachment[]) => void,
  ) {
    if (!list?.length) return;
    try {
      const picked = await filesToAttachments(Array.from(list));
      setter((prev) => [...prev, ...picked].slice(0, 5));
    } catch (e: any) {
      toast.error(e?.message ?? "Could not attach file");
    }
  }



  const inboxQ = useQuery({
    queryKey: ["admin-inbox"],
    queryFn: () => list(),
  });

  const threadQ = useQuery({
    queryKey: ["admin-inbox-thread", selected?.source, selected?.id],
    queryFn: () => thread({ data: { source: selected!.source, id: selected!.id } }),
    enabled: !!selected,
  });

  const items = useMemo(() => {
    const all = inboxQ.data?.items ?? [];
    const t = tab === "all" ? all : all.filter((i) => i.source === tab);
    if (!q.trim()) return t;
    const needle = q.toLowerCase();
    return t.filter((i) =>
      [i.recipient_name, i.recipient_email, i.preview, i.subject_hint, i.reference]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(needle)),
    );
  }, [inboxQ.data, tab, q]);

  const sendMut = useMutation({
    mutationFn: () => {
      const rec = (threadQ.data?.record ?? {}) as any;
      return send({
        data: {
          source: selected!.source,
          id: selected!.id,
          recipientEmail: rec.email || rec.contact_email || rec.client_email || "",
          recipientName: rec.full_name || rec.contact_name || rec.client_name || "",
          subject,
          body,
          attachments: replyFiles.length
            ? replyFiles.map(({ filename, contentBase64, contentType }) => ({
                filename,
                contentBase64,
                contentType,
              }))
            : undefined,
          category: replyCategory,
        },
      });
    },
    onSuccess: (r: any) => {
      if (r.ok) {
        toast.success("Reply sent");
        setSubject("");
        setBody("");
        setReplyFiles([]);
        qc.invalidateQueries({ queryKey: ["admin-inbox"] });
        qc.invalidateQueries({ queryKey: ["admin-inbox-thread", selected?.source, selected?.id] });
      } else {
        toast.error(`Reply logged but email failed: ${r.reason ?? "unknown"}`);
      }
    },
    onError: (e: any) => toast.error(e?.message ?? "Could not send"),
  });

  const sendDirectMut = useMutation({
    mutationFn: () =>
      sendDirect({
        data: {
          recipientEmail: composeTo.trim(),
          recipientName: composeToName.trim() || undefined,
          subject: composeSubject.trim(),
          body: composeBody.trim(),
          attachments: composeFiles.length
            ? composeFiles.map(({ filename, contentBase64, contentType }) => ({
                filename,
                contentBase64,
                contentType,
              }))
            : undefined,
          category: composeCategory,
        },
      }),
    onSuccess: (r: any) => {
      if (r.ok) {
        toast.success(
          r.clientCreated
            ? `Email sent to ${composeTo} — a client portal was created for them`
            : `Email sent to ${composeTo}`,
        );
        setComposeOpen(false);
        setComposeTo("");
        setComposeToName("");
        setComposeSubject("");
        setComposeBody("");
        setComposeFiles([]);
      } else {
        toast.error(`Send failed: ${r.reason ?? "unknown"}`);
      }
    },
    onError: (e: any) => toast.error(e?.message ?? "Could not send"),
  });


  function selectItem(it: any) {
    setSelected({ source: it.source, id: it.id });
    setSubject(it.subject_hint);
    setBody("");
  }

  return (
    <section>
      <header className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-xl text-navy">Unified inbox</h2>
          <p className="text-xs text-muted-foreground">
            Reply by email to enquiries, bookings, awareness requests, and contract orders. Every reply is logged.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, email, message…"
              className="w-64 rounded-md border border-input bg-background pl-8 pr-3 py-2 text-sm focus:border-gold focus:outline-none"
            />
          </div>
          <button
            onClick={() => setComposeOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-md bg-navy px-3 py-2 text-sm font-semibold text-navy-foreground hover:bg-navy/90"
          >
            <Send className="h-3 w-3" /> New email
          </button>
        </div>
      </header>

      {composeOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setComposeOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-lg rounded-lg border border-border bg-card p-5 shadow-xl"
          >
            <div className="mb-3 flex items-center justify-between">
              <h3 className="font-display text-lg text-navy">New email</h3>
              <button
                onClick={() => setComposeOpen(false)}
                className="text-sm text-muted-foreground hover:text-foreground"
              >
                Close
              </button>
            </div>
            <p className="mb-3 text-[11px] text-muted-foreground">
              Sent from noreply@nkadimengdannylcs.com using your branded template.
            </p>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const okEmail = /.+@.+\..+/.test(composeTo.trim());
                if (!okEmail) return toast.error("Enter a valid recipient email");
                if (composeSubject.trim().length < 2 || composeBody.trim().length < 2)
                  return toast.error("Subject and message are required");
                sendDirectMut.mutate();
              }}
              className="space-y-2"
            >
              <input
                type="email"
                value={composeTo}
                onChange={(e) => setComposeTo(e.target.value)}
                placeholder="Recipient email"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-navy focus:outline-none"
                required
              />
              <input
                value={composeToName}
                onChange={(e) => setComposeToName(e.target.value)}
                placeholder="Recipient name (optional)"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-navy focus:outline-none"
              />
              <div className="grid gap-2 sm:grid-cols-2">
                <select
                  aria-label="Preloaded message"
                  defaultValue=""
                  onChange={(e) =>
                    applyPreset(e.target.value, setComposeSubject, setComposeBody, setComposeCategory, {
                      ClientName: composeToName || undefined,
                    })
                  }
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-navy focus:outline-none"
                >
                  <option value="">Preloaded template…</option>
                  {presetsByGroup().map((g) => (
                    <optgroup key={g.group} label={g.group}>
                      {g.presets.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.label}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                <select
                  aria-label="Matter category"
                  value={composeCategory}
                  onChange={(e) => setComposeCategory(e.target.value)}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-navy focus:outline-none"
                >
                  {MATTER_CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>
              <input
                value={composeSubject}
                onChange={(e) => setComposeSubject(e.target.value)}
                placeholder="Subject"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-navy focus:outline-none"
              />
              <textarea
                value={composeBody}
                onChange={(e) => setComposeBody(e.target.value)}
                rows={9}
                placeholder="Write your message…"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-navy focus:outline-none"
              />
              <AttachmentPicker
                id="compose-files"
                files={composeFiles}
                onPick={(l) => pickFiles(l, setComposeFiles)}
                onRemove={(i) => setComposeFiles((p) => p.filter((_, x) => x !== i))}
              />
              <div className="flex justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => setComposeOpen(false)}
                  className="rounded-md border border-input px-3 py-2 text-sm hover:bg-muted"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={sendDirectMut.isPending}
                  className="inline-flex items-center gap-1.5 rounded-md bg-navy px-4 py-2 text-sm font-semibold text-navy-foreground hover:bg-navy/90 disabled:opacity-60"
                >
                  {sendDirectMut.isPending ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Send className="h-3 w-3" />
                  )}
                  Send email
                </button>
              </div>
            </form>
          </div>
        </div>
      )}


      <div className="mb-3 flex flex-wrap gap-1">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={
              "rounded-full border px-3 py-1 text-xs font-medium transition " +
              (tab === t.id
                ? "border-navy bg-navy text-navy-foreground"
                : "border-input bg-background text-foreground/80 hover:border-navy")
            }
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="grid gap-4 md:grid-cols-[minmax(280px,360px)_1fr]">
        <ul className="max-h-[70vh] space-y-2 overflow-y-auto pr-1">
          {inboxQ.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
          {items.map((it) => {
            const isSel = selected?.source === it.source && selected.id === it.id;
            return (
              <li key={`${it.source}:${it.id}`}>
                <button
                  onClick={() => selectItem(it)}
                  className={
                    "w-full rounded-lg border p-3 text-left transition " +
                    (isSel
                      ? "border-navy bg-navy/5"
                      : "border-border bg-card hover:border-navy/40")
                  }
                >
                  <div className="mb-1 flex items-center justify-between gap-2">
                    <span className="text-[10px] font-semibold uppercase tracking-widest text-gold">
                      {it.source_label}
                    </span>
                    <span className="text-[10px] text-muted-foreground">
                      {new Date(it.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-sm font-medium text-navy">{it.recipient_name || it.recipient_email || "—"}</p>
                  <p className="truncate text-xs text-muted-foreground">{it.preview}</p>
                  <div className="mt-1 flex items-center gap-2 text-[10px]">
                    {it.status && <span className="rounded-full bg-muted px-2 py-0.5">{it.status}</span>}
                    {it.reply_count > 0 && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-emerald-800">
                        <Mail className="h-3 w-3" /> {it.reply_count}
                      </span>
                    )}
                  </div>
                </button>
              </li>
            );
          })}
          {!inboxQ.isLoading && items.length === 0 && (
            <p className="text-sm text-muted-foreground">Nothing matches.</p>
          )}
        </ul>

        <div className="rounded-lg border border-border bg-card p-4">
          {!selected && (
            <p className="text-sm text-muted-foreground">Select an item on the left to view and reply.</p>
          )}
          {selected && threadQ.isLoading && (
            <p className="text-sm text-muted-foreground">Loading thread…</p>
          )}
          {selected && threadQ.data && (
            <div className="space-y-4">
              {selected.source !== "sent_emails" && <div className="rounded-md bg-muted/50 p-3 text-xs">
                <pre className="whitespace-pre-wrap font-sans text-[12px] text-foreground/80">
                  {JSON.stringify(threadQ.data.record, null, 2)}
                </pre>
              </div>}

              {selected.source === "sent_emails" && (
                <div className="rounded-md border border-border bg-background p-3 text-sm">
                  <p className="font-semibold text-navy">
                    {(threadQ.data.record as any)?.metadata?.subject ?? "Sent email"}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    To {(threadQ.data.record as any)?.recipient_email} · Status {(threadQ.data.record as any)?.status}
                  </p>
                  {(threadQ.data.record as any)?.metadata?.html ? (
                    <iframe
                      title="Sent email preview"
                      srcDoc={(threadQ.data.record as any).metadata.html}
                      sandbox=""
                      className="mt-3 h-[420px] w-full rounded-md border border-border bg-background"
                    />
                  ) : (
                    <p className="mt-3 whitespace-pre-wrap rounded-md border border-border bg-muted/40 p-3 text-sm text-foreground/90">
                      {(threadQ.data.record as any)?.metadata?.text ||
                        (threadQ.data.record as any)?.metadata?.body ||
                        "No rendered copy was captured for this email. Newer emails store a full preview."}
                    </p>
                  )}
                  {(() => {
                    const meta = ((threadQ.data.record as any)?.metadata ?? {}) as any;
                    const links: Array<{ name: string; url: string }> = Array.isArray(
                      meta.attachment_links,
                    )
                      ? meta.attachment_links
                      : [];
                    const names: string[] = Array.isArray(meta.attachments) ? meta.attachments : [];
                    if (!links.length && !names.length) return null;
                    return (
                      <div className="mt-3 border-t border-border pt-2">
                        <p className="mb-1 text-[10px] font-semibold uppercase text-muted-foreground">
                          Attachments
                        </p>
                        {links.length
                          ? links.map((a, i) => (
                              <a
                                key={`${a.name}-${i}`}
                                href={a.url}
                                target="_blank"
                                rel="noreferrer"
                                className="flex items-center gap-1 text-xs font-medium text-navy hover:underline"
                              >
                                <Paperclip className="h-3 w-3" /> {a.name}
                              </a>
                            ))
                          : names.map((name, i) => (
                              <p key={`${name}-${i}`} className="flex items-center gap-1 text-xs">
                                <Paperclip className="h-3 w-3" /> {name}
                              </p>
                            ))}
                      </div>
                    );
                  })()}
                </div>
              )}

              {threadQ.data.replies.length > 0 && (
                <div>
                  <p className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                    Previous replies
                  </p>
                  <ul className="space-y-2">
                    {threadQ.data.replies.map((r: any) => (
                      <li key={r.id} className="rounded-md border border-border bg-background p-3">
                        <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                          <span>To {r.recipient_email}</span>
                          <span>{new Date(r.created_at).toLocaleString()}</span>
                        </div>
                        <p className="mt-1 text-sm font-semibold text-navy">{r.subject}</p>
                        <p className="mt-1 whitespace-pre-wrap text-sm text-foreground/90">{r.body}</p>
                        <p className="mt-1 text-[10px] text-muted-foreground">Status: {r.status}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {selected.source !== "sent_emails" && <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (subject.trim().length < 2 || body.trim().length < 2) {
                    toast.error("Subject and message are required");
                    return;
                  }
                  sendMut.mutate();
                }}
                className="space-y-2"
              >
                <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                  Compose reply
                </p>
                <div className="grid gap-2 sm:grid-cols-2">
                  <select
                    aria-label="Preloaded reply"
                    defaultValue=""
                    onChange={(e) =>
                      applyPreset(e.target.value, setSubject, setBody, setReplyCategory, {
                        ClientName: threadQ.data?.record?.name ?? undefined,
                      })
                    }
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-navy focus:outline-none"
                  >
                    <option value="">Preloaded template…</option>
                    {presetsByGroup().map((g) => (
                      <optgroup key={g.group} label={g.group}>
                        {g.presets.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.label}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                  <select
                    aria-label="Matter category"
                    value={replyCategory}
                    onChange={(e) => setReplyCategory(e.target.value)}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-navy focus:outline-none"
                  >
                    {MATTER_CATEGORIES.map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </div>
                <input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Subject"
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-navy focus:outline-none"
                />
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={8}
                  placeholder="Write your reply…"
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-navy focus:outline-none"
                />
                <AttachmentPicker
                  id="reply-files"
                  files={replyFiles}
                  onPick={(l) => pickFiles(l, setReplyFiles)}
                  onRemove={(i) => setReplyFiles((p) => p.filter((_, x) => x !== i))}
                />
                <button
                  type="submit"
                  disabled={sendMut.isPending}
                  className="inline-flex items-center gap-1.5 rounded-md bg-navy px-4 py-2 text-sm font-semibold text-navy-foreground hover:bg-navy/90 disabled:opacity-60"
                >
                  {sendMut.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                  Send email
                </button>
              </form>}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function AttachmentPicker({
  id,
  files,
  onPick,
  onRemove,
}: {
  id: string;
  files: PickedAttachment[];
  onPick: (list: FileList | null) => void;
  onRemove: (index: number) => void;
}) {
  return (
    <div className="space-y-1">
      <label
        htmlFor={id}
        className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-input px-3 py-1.5 text-xs font-medium hover:bg-muted"
      >
        <Paperclip className="h-3 w-3" /> Attach documents
      </label>
      <input
        id={id}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => {
          onPick(e.target.files);
          e.target.value = "";
        }}
      />
      {files.length > 0 && (
        <ul className="space-y-1 text-[11px]">
          {files.map((f, i) => (
            <li
              key={`${f.filename}-${i}`}
              className="flex items-center justify-between rounded border border-border bg-muted/40 px-2 py-1"
            >
              <span className="truncate">
                {f.filename} · {(f.size / 1024).toFixed(0)} KB
              </span>
              <button type="button" onClick={() => onRemove(i)} aria-label={`Remove ${f.filename}`}>
                <X className="h-3 w-3 text-muted-foreground hover:text-foreground" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <p className="text-[10px] text-muted-foreground">
        Up to 5 files, 8 MB each. Recipients get secure download links valid for 30 days.
      </p>
    </div>
  );
}
